/*
 *  -----------------------------------------------------------------------
 *    <copyright file="study-overview-scrollable-grid/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React from 'react';
import { useQuery } from '@apollo/client/react';
import cn from 'clsx';
// eslint-disable-next-line max-len
import { StudyOverviewScrollableGridProps } from '@components/central-monitoring/study-summary/study-overview-scrollable-grid/study-overview-scrollable-grid.interface';
import ExpandableGridV2 from '@components/core/expandable/grid-v2';
import { ExcelExportConfig } from '@components/core/expandable/grid-v2/interfaces-v2/grid-props-v2.interface';
import Loading from '@components/core/loading';
import Error from '@components/error/error';
import { formStudyOverviewData, getStudyOverviewColumns } from './study-overview-scrollable-grid.helper';
import { useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { studyOverviewTable, studyOverviewTableVariables } from '@queries/generated/studies.generated';
import { GET_STUDY_SUMMARY_OVERVIEW_TABLE } from '@queries/studies';

const StudyOverviewScrollableGrid = ({
    studyData,
    studyId,
    currentDataSet,
    previousDataSet,
    gridViewOptions,
    switchMenu,
    inactive,
}: StudyOverviewScrollableGridProps) => {
    const { t } = useTranslation();

    const { data, loading, error } = useQuery<studyOverviewTable, studyOverviewTableVariables>(
        GET_STUDY_SUMMARY_OVERVIEW_TABLE,
        {
            variables: {
                currentDataSetId: currentDataSet?.isDataset ? currentDataSet.id : currentDataSet?.dataSetId,
                previousDataSetId: previousDataSet.id,
                studyId,
            },
        },
    );

    const gridData = formStudyOverviewData(data?.studyOverviewTable || []);
    const exportFileName = `${studyData?.study?.Fields?.['Study.number']}_StudyOverview_ComparisonDataset-${
        !previousDataSet.isDefault ? previousDataSet._dataCutDate : ''
    }_Dataset-${stringDateWithoutTimeFromUtc(studyData.dataCutDate, 'DD-MMM-YYYY')}`;

    const exportConfig: ExcelExportConfig = {
        fileName: exportFileName,
        useColumn: true,
        useData: true,
    };

    if (loading) return <Loading />;

    if (error) return <Error error={error} />;

    return (
        <>
            {(inactive || !gridData.length) && (
                <p className="warning-message">
                    {inactive ? t('cm:noOverviewTablesSelected') : t('admin:studyOverviewTablesEmptyColumnsWarning')}
                </p>
            )}
            <ExpandableGridV2
                title={t('cm:studyOverview')}
                customKey="study-overview-grid"
                className={cn({ 'grid-inactive': inactive || !gridData.length }, 'aligned-headers')}
                id="studyOverviewGrid"
                items={!inactive && gridData.length ? gridData : []}
                groupable
                scrollable="scrollable"
                reorderable
                resizable
                pageable={false}
                hideCheckbox
                hideExpand
                gridViewOptions={gridViewOptions}
                switchMenu={switchMenu}
                showFirstColumn={false}
                columns={
                    !inactive && gridData.length
                        ? getStudyOverviewColumns(data?.studyOverviewTable || [], previousDataSet, currentDataSet, t)
                        : []
                }
                exportConfig={exportConfig}
            />
        </>
    );
};

export default StudyOverviewScrollableGrid;
